import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const CHART_WIDTH_FALLBACK = 960;
const MIN_BAR_SIZE = 14;

export const CHART_COLORS = {
  equityFillStart: '#84cc16',
  equityFillMiddle: '#84cc16',
  equityFillEnd: '#84cc16',
  equityLineStart: '#4d7c0f',
  equityLineMiddle: '#4d7c0f',
  equityLineEnd: '#4d7c0f',
  equityLabel: '#556b2f',
  equityLegend: '#4d7c0f',
  positiveBarFill: '#65a30d',
  positiveBarFillEnd: '#a3e635',
  positiveBarStroke: '#65a30d',
  positiveLegend: '#65a30d',
  negativeBarFill: '#f43f5e',
  negativeBarFillEnd: '#fb7185',
  negativeBarStroke: '#be123c',
  negativeLegend: '#f43f5e',
  rightAxisLabel: '#556b2f',
  rightAxisTick: '#556b2f',
  leftAxisTick: '#556b2f',
  xAxisTick: '#556b2f',
  cursorFill: 'rgba(132, 204, 22, 0.08)',
  cursorStroke: 'rgba(132, 204, 22, 0.15)',
  referenceLine: '#cbd5a5',
  grid: '#d9f99d',
};

const formatCompactNumber = (value, digits = 2) =>
  Number(value).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const parseTimestamp = (timestamp) => {
  if (!timestamp) {
    return null;
  }

  const normalized = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatTradeTimestamp = (
  timestamp,
  { short = false, includeSeconds = false } = {},
) => {
  const parsed = parseTimestamp(timestamp);

  if (!parsed) {
    return timestamp || 'Unknown';
  }

  const hasTime =
    parsed.getHours() !== 0 || parsed.getMinutes() !== 0 || parsed.getSeconds() !== 0;

  if (short) {
    if (hasTime) {
      return parsed.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: includeSeconds ? '2-digit' : undefined,
      });
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: hasTime ? '2-digit' : undefined,
    minute: hasTime ? '2-digit' : undefined,
    second: hasTime && includeSeconds ? '2-digit' : undefined,
  });
};

export const buildTradeLabels = (data = []) => {
  const minuteLabels = data.map((item) =>
    formatTradeTimestamp(item.timestamp, { short: true }),
  );
  const minuteLabelCounts = minuteLabels.reduce((acc, label) => {
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  const secondLabels = data.map((item) =>
    formatTradeTimestamp(item.timestamp, { short: true, includeSeconds: true }),
  );
  const secondLabelCounts = secondLabels.reduce((acc, label) => {
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  return data.map((_, index) => {
    const minuteLabel = minuteLabels[index];
    if (minuteLabelCounts[minuteLabel] === 1) {
      return minuteLabel;
    }

    const secondLabel = secondLabels[index];
    if (secondLabelCounts[secondLabel] === 1) {
      return secondLabel;
    }

    return `Trade ${index + 1}`;
  });
};

export const calculateAdaptiveBarSize = (chartWidth, pointCount) => {
  const safeWidth = chartWidth > 0 ? chartWidth : CHART_WIDTH_FALLBACK;

  if (!pointCount || pointCount <= 0) {
    return 24;
  }

  const usableWidth = Math.max(safeWidth - 120, 180);
  const slotWidth = usableWidth / pointCount;
  const fillRatio =
    pointCount <= 2 ? 0.94 : pointCount <= 4 ? 0.88 : pointCount <= 8 ? 0.74 : 0.58;
  const rawBarSize = slotWidth * fillRatio;
  const maxBarSize = Math.max(MIN_BAR_SIZE, slotWidth - 6);

  return Math.round(Math.min(maxBarSize, Math.max(MIN_BAR_SIZE, rawBarSize)));
};

export const calculateXAxisInterval = (pointCount) => {
  if (pointCount <= 8) {
    return 0;
  }

  if (pointCount <= 24) {
    return 'preserveStartEnd';
  }

  return Math.ceil(pointCount / 8);
};

export const calculateBarCategoryGap = (pointCount) => {
  if (!pointCount || pointCount <= 1) {
    return '2%';
  }

  if (pointCount <= 3) {
    return '4%';
  }

  if (pointCount <= 6) {
    return '8%';
  }

  if (pointCount <= 12) {
    return '16%';
  }

  return '24%';
};

const CustomTooltip = ({ active, payload, chartMode = 'default' }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0]?.payload;
  if (!data) {
    return null;
  }

  const isProfile = chartMode === 'profile';
  const runningTotal = isProfile ? data.period_cumulative_pnl : data.cumulative_equity;
  const pnl = data.pnl;

  if (data.is_anchor) {
    return (
      <div className="min-w-[220px] rounded-2xl border border-olive-100 bg-white/95 p-4 text-sm shadow-xl backdrop-blur-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-olive-500">Period start</p>
        <p className="mt-2 text-sm font-semibold text-olive-900">Cumulative PnL begins at 0</p>
      </div>
    );
  }

  return (
    <div className="min-w-[240px] rounded-2xl border border-olive-100 bg-white/95 p-4 text-sm shadow-xl backdrop-blur-sm">
      <div className="mb-3 border-b border-olive-100 pb-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-olive-500">
          Close #{data.trade_number}
        </p>
        <p className="mt-1 text-sm font-semibold text-olive-900">
          {formatTradeTimestamp(data.timestamp, { includeSeconds: true })}
        </p>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-olive-600">Trade PnL</span>
          <span
            className={`font-bold ${
              pnl >= 0 ? 'text-olive-700' : 'text-olive-500'
            }`}
          >
            {pnl >= 0 ? '+' : ''}
            {formatCompactNumber(pnl)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-4">
          <span className="text-olive-600">{isProfile ? 'Running total' : 'Realized equity'}</span>
          <span className="font-bold text-olive-900">{formatCompactNumber(runningTotal)}</span>
        </div>
      </div>
    </div>
  );
};

const StatsPanel = ({ data, title }) => {
  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    const totalPnL = data.reduce((sum, trade) => sum + trade.pnl, 0);
    const finalEquity = data[data.length - 1]?.cumulative_equity || 0;
    const initialEquity = data[0]?.cumulative_equity
      ? data[0].cumulative_equity - data[0].pnl
      : finalEquity - totalPnL;

    let maxEquity = initialEquity;
    let maxDrawdown = 0;
    let maxDrawdownPercent = 0;

    data.forEach((trade) => {
      if (trade.cumulative_equity > maxEquity) {
        maxEquity = trade.cumulative_equity;
      }

      const currentDrawdown = maxEquity - trade.cumulative_equity;
      const currentDrawdownPercent = maxEquity > 0 ? (currentDrawdown / maxEquity) * 100 : 0;

      if (currentDrawdown > maxDrawdown) {
        maxDrawdown = currentDrawdown;
        maxDrawdownPercent = currentDrawdownPercent;
      }
    });

    const totalTrades = data.length;
    const winningTrades = data.filter((trade) => trade.pnl > 0).length;
    const losingTrades = data.filter((trade) => trade.pnl < 0).length;
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

    return {
      totalPnL,
      maxDrawdown,
      maxDrawdownPercent,
      totalTrades,
      winningTrades,
      losingTrades,
      winRate,
    };
  }, [data]);

  if (!stats) {
    return null;
  }

  return (
    <div className="border-b border-olive-100 bg-gradient-to-r from-olive-50 via-white to-olive-50 p-6">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-lg font-bold text-olive-900">{title}</h3>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-olive-100 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive-500">
            Total PnL
          </p>
          <p className={`mt-3 text-2xl font-bold ${stats.totalPnL >= 0 ? 'text-olive-700' : 'text-olive-500'}`}>
            {stats.totalPnL >= 0 ? '+' : ''}
            {formatCompactNumber(stats.totalPnL)}
          </p>
        </div>

        <div className="rounded-2xl border border-olive-100 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive-500">
            Max Drawdown
          </p>
          <p className="mt-3 text-2xl font-bold text-olive-900">
            {formatCompactNumber(stats.maxDrawdown)}
          </p>
          <p className="mt-2 text-xs text-olive-500">{stats.maxDrawdownPercent.toFixed(2)}%</p>
        </div>

        <div className="rounded-2xl border border-olive-100 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive-500">
            Closed Trades
          </p>
          <p className="mt-3 text-2xl font-bold text-olive-900">
            {stats.totalTrades.toLocaleString('en-US')}
          </p>
          <p className="mt-2 text-xs text-olive-500">
            Win rate {stats.winRate.toFixed(1)}% | {stats.winningTrades}W / {stats.losingTrades}L
          </p>
        </div>
      </div>
    </div>
  );
};

export const AxisLegend = ({ chartMode = 'default' }) => {
  const isProfile = chartMode === 'profile';

  return (
  <div className="mb-4 flex flex-col gap-2 rounded-2xl border border-olive-100 bg-olive-50/50 p-3 text-xs text-olive-700 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-wrap items-center gap-3">
      <span className="inline-flex items-center gap-2 font-semibold">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: CHART_COLORS.equityLegend }}
        />
        {isProfile ? 'Running total' : 'Realized equity'}
      </span>
      <span className="inline-flex items-center gap-2 font-semibold">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: CHART_COLORS.positiveLegend }}
        />
        {isProfile ? 'Winning close' : '+PnL'}
      </span>
      <span className="inline-flex items-center gap-2 font-semibold">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: CHART_COLORS.negativeLegend }}
        />
        {isProfile ? 'Losing close' : '-PnL'}
      </span>
    </div>
    {isProfile && (
      <span className="text-[11px] font-medium text-olive-600">
        One step per closed trade in the selected period
      </span>
    )}
  </div>
  );
};

const TradingPerformanceChart = ({
  data,
  title = 'Trading Report',
  showStatsPanel = true,
  xAxisMode = 'trade',
  chartMode = 'default',
}) => {
  const chartWrapperRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const element = chartWrapperRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setChartWidth(element.clientWidth || 0);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => updateWidth());
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const isProfile = chartMode === 'profile';
  const isTimeSeries = !isProfile && xAxisMode === 'time';

  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    if (isProfile) {
      return data.map((item) => ({
        ...item,
        pnl: item.is_anchor ? null : item.pnl,
      }));
    }

    const tradeLabels = buildTradeLabels(data);

    return data
      .map((item, index) => {
        const parsedTime = parseTimestamp(item.timestamp);
        const timeValue = parsedTime ? parsedTime.getTime() : null;
        const cumulativeEquity =
          item.cumulative_equity ??
          data.slice(0, index + 1).reduce((sum, trade) => sum + trade.pnl, 0);

        return {
          ...item,
          trade_number: index + 1,
          trade_label: tradeLabels[index],
          timeValue,
          cumulative_equity: cumulativeEquity,
        };
      })
      .filter((item) => (isTimeSeries ? item.timeValue !== null : true));
  }, [data, isProfile, isTimeSeries]);

  const { valueDomain, equityDomain, pnlDomain } = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return {
        valueDomain: [-10, 10],
        equityDomain: [0, 100],
        pnlDomain: [-10, 10],
      };
    }

    if (isProfile) {
      const values = processedData.flatMap((item) =>
        [item.period_cumulative_pnl, item.pnl].filter((value) => value !== null && value !== undefined),
      );
      const minValue = Math.min(0, ...values);
      const maxValue = Math.max(0, ...values);
      const padding = Math.max((maxValue - minValue) * 0.12, 1);
      const domain = [minValue - padding, maxValue + padding];
      return {
        valueDomain: domain,
        equityDomain: domain,
        pnlDomain: domain,
      };
    }

    const equityValues = processedData.map((item) => item.cumulative_equity);
    const pnlValues = processedData.map((item) => item.pnl);
    const minEquity = Math.min(...equityValues);
    const maxEquity = Math.max(...equityValues);
    const minPnl = Math.min(...pnlValues);
    const maxPnl = Math.max(...pnlValues);
    const equityPadding = Math.max((maxEquity - minEquity) * 0.08, 1);
    const pnlPadding = Math.max(Math.max(Math.abs(minPnl), Math.abs(maxPnl)) * 0.12, 1);

    return {
      valueDomain: [minEquity - equityPadding, maxEquity + equityPadding],
      equityDomain: [minEquity - equityPadding, maxEquity + equityPadding],
      pnlDomain: [
        minPnl < 0 ? minPnl - pnlPadding : -pnlPadding,
        maxPnl > 0 ? maxPnl + pnlPadding : pnlPadding,
      ],
    };
  }, [isProfile, processedData]);

  const timeDomain = useMemo(() => {
    if (!isTimeSeries || processedData.length === 0) {
      return ['dataMin', 'dataMax'];
    }

    const values = processedData.map((item) => item.timeValue);
    const minTime = Math.min(...values);
    const maxTime = Math.max(...values);
    const padding = Math.max((maxTime - minTime) * 0.04, 60 * 1000);
    return [minTime - padding, maxTime + padding];
  }, [isTimeSeries, processedData]);

  const markerData = useMemo(
    () => processedData.filter((item) => !item.is_baseline && item.pnl !== 0),
    [processedData],
  );

  const barSize = useMemo(
    () => calculateAdaptiveBarSize(chartWidth, isProfile ? Math.max(processedData.length - 1, 1) : processedData.length),
    [chartWidth, isProfile, processedData.length],
  );
  const xAxisInterval = useMemo(
    () => calculateXAxisInterval(processedData.length),
    [processedData.length],
  );
  const barCategoryGap = useMemo(
    () => calculateBarCategoryGap(processedData.length),
    [processedData.length],
  );

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[360px] w-full items-center justify-center rounded-2xl border border-olive-100 bg-white">
        <div className="text-center">
          <p className="text-lg font-semibold text-olive-900">No chart data available</p>
          <p className="mt-2 text-sm text-olive-500">
            Closed trades with realized PnL will appear here.
          </p>
        </div>
      </div>
    );
  }

  const formatAxisNumber = (value) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
    }).format(value);

  return (
    <div className="overflow-hidden rounded-2xl border border-olive-100 bg-white shadow-sm">
      {showStatsPanel && <StatsPanel data={processedData} title={title} />}

      <div className="bg-gradient-to-b from-white to-olive-50/30 p-4 sm:p-6">
        <AxisLegend chartMode={chartMode} />
        <div
          ref={chartWrapperRef}
          className="h-[440px] rounded-2xl border border-olive-100 bg-white p-3 sm:p-4"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={processedData}
              margin={{ top: 24, right: isProfile ? 18 : isTimeSeries ? 18 : 34, left: 18, bottom: 18 }}
              barCategoryGap={barCategoryGap}
              barGap={0}
            >
              <defs>
                <linearGradient id="profile-equity-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.equityFillStart} stopOpacity={0.22} />
                  <stop offset="50%" stopColor={CHART_COLORS.equityFillMiddle} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={CHART_COLORS.equityFillEnd} stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="profile-equity-line" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.equityLineStart} stopOpacity={1} />
                  <stop offset="50%" stopColor={CHART_COLORS.equityLineMiddle} stopOpacity={1} />
                  <stop offset="100%" stopColor={CHART_COLORS.equityLineEnd} stopOpacity={1} />
                </linearGradient>
                <linearGradient id="profile-bar-fill-positive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.positiveBarFill} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={CHART_COLORS.positiveBarFillEnd} stopOpacity={0.45} />
                </linearGradient>
                <linearGradient id="profile-bar-fill-negative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.negativeBarFill} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={CHART_COLORS.negativeBarFillEnd} stopOpacity={0.45} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 4"
                stroke={CHART_COLORS.grid}
                strokeOpacity={0.9}
                vertical={false}
              />

              <XAxis
                dataKey={isTimeSeries ? 'timeValue' : 'trade_label'}
                type={isTimeSeries ? 'number' : 'category'}
                scale={isTimeSeries ? 'time' : undefined}
                domain={isTimeSeries ? timeDomain : undefined}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: CHART_COLORS.xAxisTick, fontWeight: 600 }}
                interval={isTimeSeries ? 'preserveStartEnd' : xAxisInterval}
                minTickGap={isTimeSeries ? 28 : 18}
                height={42}
                tickFormatter={
                  isTimeSeries
                    ? (value) =>
                        formatTradeTimestamp(new Date(value).toISOString().replace('T', ' ').slice(0, 19), {
                          short: true,
                        })
                    : undefined
                }
              />

              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: CHART_COLORS.leftAxisTick, fontWeight: 600 }}
                tickFormatter={formatAxisNumber}
                domain={isProfile ? valueDomain : equityDomain}
                width={74}
                label={{
                  value: isProfile ? 'PnL' : 'Realized equity',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  fill: CHART_COLORS.equityLabel,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              />

              {!isProfile && !isTimeSeries && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: CHART_COLORS.rightAxisTick, fontWeight: 600 }}
                  tickFormatter={formatAxisNumber}
                  domain={pnlDomain}
                  width={74}
                  label={{
                    value: 'Trade PnL',
                    angle: 90,
                    position: 'insideRight',
                    offset: 2,
                    fill: CHART_COLORS.rightAxisLabel,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                />
              )}

              <Tooltip
                content={<CustomTooltip chartMode={chartMode} />}
                cursor={{ fill: CHART_COLORS.cursorFill, stroke: CHART_COLORS.cursorStroke }}
              />

              <ReferenceLine
                yAxisId="left"
                y={0}
                stroke={CHART_COLORS.referenceLine}
                strokeDasharray="4 6"
              />

              {isProfile && (
                <Bar
                  yAxisId="left"
                  dataKey="pnl"
                  barSize={barSize}
                  radius={[6, 6, 0, 0]}
                  animationDuration={250}
                >
                  {processedData.map((entry, index) => (
                    <Cell
                      key={`pnl-cell-${entry.trade_number}-${index}`}
                      fill={
                        entry.pnl == null
                          ? 'transparent'
                          : entry.pnl >= 0
                            ? 'url(#profile-bar-fill-positive)'
                            : 'url(#profile-bar-fill-negative)'
                      }
                      fillOpacity={entry.pnl == null ? 0 : 1}
                      stroke={
                        entry.pnl == null
                          ? 'transparent'
                          : entry.pnl >= 0
                            ? CHART_COLORS.positiveBarStroke
                            : CHART_COLORS.negativeBarStroke
                      }
                      strokeOpacity={entry.pnl == null ? 0 : 0.25}
                    />
                  ))}
                </Bar>
              )}

              {!isProfile && !isTimeSeries && (
                <Bar
                  yAxisId="right"
                  dataKey="pnl"
                  barSize={barSize}
                  radius={[6, 6, 0, 0]}
                  animationDuration={250}
                >
                  {processedData.map((entry, index) => (
                    <Cell
                      key={`pnl-cell-right-${entry.trade_number}-${index}`}
                      fill={entry.pnl >= 0 ? 'url(#profile-bar-fill-positive)' : 'url(#profile-bar-fill-negative)'}
                      fillOpacity={1}
                      stroke={entry.pnl >= 0 ? CHART_COLORS.positiveBarStroke : CHART_COLORS.negativeBarStroke}
                      strokeOpacity={0.25}
                    />
                  ))}
                </Bar>
              )}

              {isProfile ? (
                <Line
                  yAxisId="left"
                  type="stepAfter"
                  dataKey="period_cumulative_pnl"
                  stroke={CHART_COLORS.equityLineMiddle}
                  strokeWidth={3}
                  dot={{ r: 4, fill: CHART_COLORS.equityLineMiddle, stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{
                    r: 5,
                    fill: CHART_COLORS.equityLineMiddle,
                    stroke: '#fff',
                    strokeWidth: 3,
                  }}
                />
              ) : (
                <Area
                  yAxisId="left"
                  type={isTimeSeries ? 'linear' : 'monotone'}
                  dataKey="cumulative_equity"
                  stroke="url(#profile-equity-line)"
                  strokeWidth={3}
                  fill="url(#profile-equity-fill)"
                  dot={isTimeSeries ? false : processedData.length <= 12}
                  activeDot={{
                    r: 5,
                    fill: CHART_COLORS.equityLineMiddle,
                    stroke: '#fff',
                    strokeWidth: 3,
                  }}
                />
              )}

              {isTimeSeries && (
                <Scatter
                  yAxisId="left"
                  data={markerData}
                  dataKey="cumulative_equity"
                  name="Trade markers"
                  shape={(props) => {
                    const { cx, cy, payload } = props;
                    if (cx == null || cy == null) {
                      return null;
                    }

                    const fill =
                      payload.pnl >= 0 ? CHART_COLORS.positiveLegend : CHART_COLORS.negativeLegend;

                    return <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#fff" strokeWidth={2} />;
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TradingPerformanceChart;
